import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight, Eye, Wallet, ShieldCheck, Sparkles, Users, Building2,
  CheckCircle2, FileText, Send, TrendingUp, ChevronRight, Instagram,
  Music2, Youtube, Info, Lock, Ban,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";

/* ------------------------------------------------------------------ */
/*  Types + data                                                       */
/* ------------------------------------------------------------------ */

type PublicCampaign = {
  id: string;
  title: string;
  category: string | null;
  platform: string | null;
  payout_rate_per_1k: number | null;
  total_budget: number | null;
  creator_limit: number | null;
  end_date: string | null;
  brand_name: string | null;
};

const PLATFORM_LABEL: Record<string, string> = {
  tiktok: "TikTok",
  instagram_reels: "Instagram Reels",
  youtube_shorts: "YouTube Shorts",
};

const CATEGORY_LABEL: Record<string, string> = {
  tech: "Teknologji",
  beauty: "Bukuri",
  fashion: "Modë",
  food: "Ushqim",
  fitness: "Fitness",
  lifestyle: "Lifestyle",
  business: "Biznes",
  education: "Edukim",
  entertainment: "Argëtim",
  other: "Tjetër",
};

const eur = (n: number) =>
  new Intl.NumberFormat("sq-AL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const formatDeadline = (iso: string | null) => {
  if (!iso) return "Pa afat";
  try {
    return new Intl.DateTimeFormat("sq-AL", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/* ------------------------------------------------------------------ */
/*  Homepage                                                           */
/* ------------------------------------------------------------------ */

const Index = () => {
  const [campaigns, setCampaigns] = useState<PublicCampaign[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("id, title, category, platform, payout_rate_per_1k, total_budget, creator_limit, end_date, brand_profiles(brand_name)")
        .eq("status", "live")
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(6);
      if (cancelled) return;
      if (error || !data) {
        setCampaigns([]);
        return;
      }
      const mapped: PublicCampaign[] = data
        .map((r: {
          id: string; title: string; category: string | null; platform: string | null;
          payout_rate_per_1k: number | null; total_budget: number | null;
          creator_limit: number | null; end_date: string | null;
          brand_profiles: { brand_name: string | null } | { brand_name: string | null }[] | null;
        }) => {
          const bp = r.brand_profiles;
          const brand_name = Array.isArray(bp) ? bp[0]?.brand_name ?? null : bp?.brand_name ?? null;
          return {
            id: r.id, title: r.title, category: r.category, platform: r.platform,
            payout_rate_per_1k: r.payout_rate_per_1k, total_budget: r.total_budget,
            creator_limit: r.creator_limit, end_date: r.end_date, brand_name,
          };
        })
        .filter((c) => c.brand_name && c.title && c.title.trim().length >= 4 && (c.payout_rate_per_1k ?? 0) > 0)
        .slice(0, 3);
      setCampaigns(mapped);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      <Hero />
      <Marquee />
      <ValueCards />
      <CampaignsSection campaigns={campaigns} />
      <CalculatorSection />
      <ProcessSection />
      <TrustSection />
      <FaqPreview />
      <FinalCta />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  HERO                                                               */
/* ------------------------------------------------------------------ */

const Hero = () => (
  <section className="relative overflow-hidden pt-16 lg:pt-24 pb-20 lg:pb-28">
    <div aria-hidden className="absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-acid/40 blur-[80px] pointer-events-none" />
    <div className="page-container grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center relative">
      <div>
        <span className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-ink bg-paper-strong px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-wider text-ink">
          <span className="h-1.5 w-1.5 rounded-full bg-orange" aria-hidden /> Brand × Creator — Shqipëri &amp; Kosovë · EUR
        </span>
        <h1 className="mt-6 text-[44px] leading-[1.02] md:text-[64px] lg:text-[76px] font-black tracking-[-0.035em] text-ink">
          Fushata të matshme.<br />
          <span className="inline-block bg-acid px-2 -mx-2 border-[1.5px] border-ink rounded-md">Pagesa transparente.</span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-ink-soft leading-relaxed max-w-xl">
          Krebra lidh brandet me creator-at për fushata të matshme dhe pagesa transparente. Tarifë bazë e sigurt plus bonus performance mbi shikimet e verifikuara — <strong className="text-ink">15% vetëm mbi payout-in real</strong> të creator-it.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/signup" className="btn-pill btn-pill-violet">
            Bëhu creator <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/signup" className="btn-pill btn-pill-acid">
            Lanço si brand <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[13px] font-medium text-ink-soft">
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--k-success))]" /> Buxhet i para-financuar</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--k-success))]" /> Kushte të pranuara që në aplikim</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--k-success))]" /> Matje manuale + API, pa surpriza</li>
        </ul>
      </div>
      <HeroMockup />
    </div>
  </section>
);

const HeroMockup = () => (
  <div className="relative mx-auto lg:mx-0 lg:justify-self-end w-full max-w-[420px]">
    <div className="card-ink-hard-lg tilt-right p-5 md:p-6 bg-paper-strong">
      <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-ink-soft">
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[hsl(var(--k-success))]" aria-hidden /> Fushatë aktive</span>
        <span>#KR-208</span>
      </div>
      <p className="mt-3 text-xl font-black text-ink leading-tight">Kolla e Verës — Reel Challenge</p>
      <p className="mt-1 text-sm text-muted-foreground">Bora Beverages · Instagram Reels · Prishtinë + Tiranë</p>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <MockStat label="Bazë / creator" value="60€" tint="acid" />
        <MockStat label="Bonus / 1K views" value="3€" tint="violet" />
        <MockStat label="Tavan bonusi" value="180€" tint="paper" />
        <MockStat label="Slota të lira" value="7 / 20" tint="paper" />
      </div>
      <div className="mt-5 rounded-2xl border-[1.5px] border-ink/20 p-4 bg-paper">
        <p className="text-[11px] font-bold uppercase tracking-wider text-ink-soft">Progresi i shikimeve</p>
        <div className="mt-2 h-2.5 w-full rounded-full bg-[hsl(var(--ink)/0.1)] overflow-hidden">
          <div className="h-full bg-ink" style={{ width: "62%" }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-[12px] text-ink-soft">
          <span>124,300 views të verifikuara</span>
          <span className="font-semibold text-ink">62%</span>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
        <Info className="h-3.5 w-3.5" aria-hidden />
        Të dhëna ilustruese — figura e panelit të brandit.
      </div>
    </div>
    <div aria-hidden className="absolute -top-4 -left-4 h-12 w-12 rounded-2xl bg-orange border-[1.5px] border-ink flex items-center justify-center text-ink font-black">€</div>
    <div aria-hidden className="absolute -bottom-4 -right-4 h-12 w-12 rounded-full bg-violet border-[1.5px] border-ink flex items-center justify-center text-white">
      <Sparkles className="h-5 w-5" />
    </div>
  </div>
);

const MockStat = ({ label, value, tint }: { label: string; value: string; tint: "acid" | "violet" | "paper" }) => {
  const bg = tint === "acid" ? "bg-acid" : tint === "violet" ? "bg-violet text-white" : "bg-paper";
  return (
    <div className={`rounded-xl border-[1.5px] border-ink p-3 ${bg}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-lg font-black tracking-tight">{value}</p>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  MARQUEE                                                            */
/* ------------------------------------------------------------------ */

const MARQUEE_ITEMS = [
  "Pa premtime virale", "★", "Views të verifikuara", "★", "Buxhet i para-financuar", "★",
  "Kushte transparente", "★", "Pagesa në EUR", "★", "Shqipëri × Kosovë", "★",
];

const Marquee = () => (
  <div className="marquee" role="marquee" aria-label="Parimet e Krebra-s">
    <div className="marquee-track">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <span key={i}>{item}</span>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  VALUE CARDS                                                        */
/* ------------------------------------------------------------------ */

const ValueCards = () => (
  <section className="py-20 lg:py-28">
    <div className="page-container">
      <SectionEyebrow>Pse Krebra</SectionEyebrow>
      <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-ink max-w-2xl">
        Një marketplace i ndërtuar për të dyja anët.
      </h2>
      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        <article className="card-ink-hard-lg p-8 md:p-10 bg-violet text-white">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl bg-white/15 border-[1.5px] border-white/40 flex items-center justify-center">
              <Users className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-[12px] font-bold uppercase tracking-widest">Për creator-at</span>
          </div>
          <h3 className="mt-6 text-3xl md:text-4xl font-black tracking-tight">
            Puno me brande serioze. Merr paguar për performancë.
          </h3>
          <ul className="mt-6 space-y-3 text-[15px] text-white/90">
            {[
              "Tarifë bazë e garantuar për çdo publikim të miratuar.",
              "Bonus performance mbi views të verifikuara, me tavan të qartë.",
              "Kushtet, të drejtat e përdorimit dhe afati — të pranuara para punës.",
              "Pagesa përmes POK, me referencë transaksioni të ndjekshme.",
            ].map((t) => (
              <li key={t} className="flex gap-3"><CheckCircle2 className="h-5 w-5 shrink-0 text-acid mt-0.5" aria-hidden />{t}</li>
            ))}
          </ul>
          <Link to="/for-creators" className="btn-pill mt-8 bg-white text-ink">
            Mëso për creator-at <ArrowRight className="h-4 w-4" />
          </Link>
        </article>

        <article className="card-ink-hard-lg p-8 md:p-10 bg-acid text-ink">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl bg-ink/10 border-[1.5px] border-ink/30 flex items-center justify-center">
              <Building2 className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-[12px] font-bold uppercase tracking-widest">Për brandet</span>
          </div>
          <h3 className="mt-6 text-3xl md:text-4xl font-black tracking-tight">
            Paguaj për rezultate, jo për premtime.
          </h3>
          <ul className="mt-6 space-y-3 text-[15px] text-ink-soft">
            {[
              "Krijo fushatë social ose UGC me bazë + bonus + tavan.",
              "Para-financo buxhetin nëpërmjet POK; nis kur creator-at pranohen.",
              "Rishiko draft-et në privatësi para publikimit.",
              "Matje manuale + API, raport final i qartë — 15% vetëm mbi payout-in real.",
            ].map((t) => (
              <li key={t} className="flex gap-3"><CheckCircle2 className="h-5 w-5 shrink-0 text-ink mt-0.5" aria-hidden />{t}</li>
            ))}
          </ul>
          <Link to="/for-brands" className="btn-pill btn-pill-ink mt-8">
            Mëso për brandet <ArrowRight className="h-4 w-4" />
          </Link>
        </article>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  CAMPAIGNS SECTION                                                  */
/* ------------------------------------------------------------------ */

const platformIcon = (platform: string | null) => {
  if (platform === "tiktok") return <Music2 className="h-3.5 w-3.5" aria-hidden />;
  if (platform === "instagram_reels") return <Instagram className="h-3.5 w-3.5" aria-hidden />;
  if (platform === "youtube_shorts") return <Youtube className="h-3.5 w-3.5" aria-hidden />;
  return <TrendingUp className="h-3.5 w-3.5" aria-hidden />;
};

const CampaignsSection = ({ campaigns }: { campaigns: PublicCampaign[] | null }) => (
  <section className="py-20 lg:py-24 bg-paper-strong border-y border-ink/20">
    <div className="page-container">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <SectionEyebrow>Fushata aktive</SectionEyebrow>
          <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-ink max-w-2xl">
            Çfarë po zhvillohet tani në Krebra.
          </h2>
        </div>
        <Link to="/signup" className="btn-pill">Shiko të gjitha <ArrowRight className="h-4 w-4" /></Link>
      </div>

      <div className="mt-12">
        {campaigns === null ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card-ink p-6 h-64 animate-pulse bg-paper" aria-hidden />
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <div className="card-ink p-10 text-center">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-acid border-[1.5px] border-ink flex items-center justify-center">
              <Info className="h-6 w-6 text-ink" aria-hidden />
            </div>
            <h3 className="mt-5 text-xl font-black text-ink">Ende s'ka fushata publike aktive.</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              Brandet janë duke përgatitur fushatat e para në Krebra. Regjistrohu si creator dhe do të njoftohemi sa hapet e para në rajonin tënd.
            </p>
            <Link to="/signup" className="btn-pill btn-pill-ink mt-6">Regjistrohu si creator</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {campaigns.map((c) => (
              <CampaignCard key={c.id} c={c} />
            ))}
          </div>
        )}
      </div>
    </div>
  </section>
);

const CampaignCard = ({ c }: { c: PublicCampaign }) => {
  const catLabel = c.category ? CATEGORY_LABEL[c.category] ?? c.category : "—";
  const platLabel = c.platform ? PLATFORM_LABEL[c.platform] ?? c.platform : "—";
  const rate = c.payout_rate_per_1k ?? 0;
  const budget = c.total_budget ?? 0;
  return (
    <article className="card-ink-hard p-6 bg-paper-strong flex flex-col">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ink text-[hsl(var(--paper-strong))] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
          {platformIcon(c.platform)} {platLabel}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">{catLabel}</span>
      </div>
      <p className="mt-4 text-[12px] font-bold uppercase tracking-wider text-muted-foreground">{c.brand_name}</p>
      <h3 className="mt-1 text-lg font-black text-ink leading-tight line-clamp-2 min-h-[3rem]">{c.title}</h3>
      <div className="mt-5 grid grid-cols-2 gap-2.5">
        <div className="rounded-xl border-[1.5px] border-ink/20 p-3 bg-acid">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">EUR / 1K views</p>
          <p className="text-xl font-black text-ink">{eur(rate)}</p>
        </div>
        <div className="rounded-xl border-[1.5px] border-ink/20 p-3 bg-paper">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">Buxhet total</p>
          <p className="text-xl font-black text-ink">{eur(budget)}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-[12px] text-ink-soft">
        <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" aria-hidden /> {c.creator_limit ?? "—"} slota</span>
        <span>Afati: {formatDeadline(c.end_date)}</span>
      </div>
      <Link to="/signup" className="btn-pill btn-pill-ink mt-6 justify-center text-[14px]">
        Apliko <ArrowRight className="h-4 w-4" />
      </Link>
    </article>
  );
};

/* ------------------------------------------------------------------ */
/*  CALCULATOR                                                         */
/* ------------------------------------------------------------------ */

const CalculatorSection = () => {
  const [base, setBase] = useState(60);
  const [views, setViews] = useState(120_000);
  const [rate, setRate] = useState(3);
  const [cap, setCap] = useState(180);

  const { thousands, uncappedBonus, bonus, total, feePlatform, net } = useMemo(() => {
    const t = Math.max(0, Math.floor(views / 1000));
    const ub = t * rate;
    const b = Math.min(ub, cap);
    const g = base + b;
    const fee = Math.round(g * 0.15 * 100) / 100;
    const n = Math.round((g - fee) * 100) / 100;
    return { thousands: t, uncappedBonus: ub, bonus: b, total: g, feePlatform: fee, net: n };
  }, [base, views, rate, cap]);

  return (
    <section className="py-20 lg:py-28">
      <div className="page-container">
        <SectionEyebrow>Llogaritësi</SectionEyebrow>
        <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-ink max-w-3xl">
          Sa mund të fitosh për një publikim.
        </h2>
        <p className="mt-4 text-ink-soft max-w-2xl">
          Formula: <code className="rounded bg-[hsl(var(--ink)/0.06)] px-1.5 py-0.5 text-[13px]">bazë + min(⌊views ÷ 1000⌋ × EUR/1K, tavan bonusi)</code>. Vlerësim, jo garanci — pagesa reale bazohet vetëm në views të verifikuara.
        </p>

        <div className="mt-10 grid lg:grid-cols-[1.1fr_1fr] gap-6">
          <div className="card-ink-hard p-6 md:p-8 bg-paper-strong space-y-6">
            <CalcField label="Tarifa bazë (EUR)" hint="Paguhet gjithmonë kur drafti miratohet.">
              <input type="number" min={0} step={5} value={base} onChange={(e) => setBase(Math.max(0, Number(e.target.value) || 0))} className="calc-input" aria-label="Tarifa bazë në EUR" />
            </CalcField>
            <CalcField label={`Views të verifikuara: ${views.toLocaleString("sq-AL")}`} hint="Numri i shikimeve të konfirmuara nga Krebra.">
              <input type="range" min={0} max={1_000_000} step={5000} value={views} onChange={(e) => setViews(Number(e.target.value))} className="w-full accent-[hsl(var(--violet))]" aria-label="Views të verifikuara" />
            </CalcField>
            <div className="grid grid-cols-2 gap-4">
              <CalcField label="EUR për 1 000 views" hint="Vendoset nga brand-i në kushtet e fushatës.">
                <input type="number" min={0} step={0.1} value={rate} onChange={(e) => setRate(Math.max(0, Number(e.target.value) || 0))} className="calc-input" aria-label="EUR për 1000 views" />
              </CalcField>
              <CalcField label="Tavan bonusi (EUR)" hint="Bonusi nuk kalon këtë vlerë.">
                <input type="number" min={0} step={10} value={cap} onChange={(e) => setCap(Math.max(0, Number(e.target.value) || 0))} className="calc-input" aria-label="Tavan bonusi" />
              </CalcField>
            </div>
            <p className="text-[12px] text-muted-foreground flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
              Fraksionet e mijëshes nuk paguhen — vetëm 1K komplete numërohen si bonus.
            </p>
          </div>

          <div className="card-ink-hard-lg p-6 md:p-8 bg-ink text-[hsl(var(--paper-strong))] space-y-5">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--acid))]">Vlerësim — jo garanci</p>
            <CalcRow label="Views të plota (1K)" value={thousands.toLocaleString("sq-AL")} />
            <CalcRow label="Bonus para tavanit" value={eur(uncappedBonus)} />
            <CalcRow label="Bonus final (pas tavanit)" value={eur(bonus)} highlight={uncappedBonus > cap} />
            <div className="my-2 border-t border-[hsl(var(--paper)/0.15)]" />
            <CalcRow label="Bruto (bazë + bonus)" value={eur(total)} />
            <CalcRow label="Tarifa Krebra (15%)" value={`− ${eur(feePlatform)}`} muted />
            <div className="rounded-2xl bg-acid text-ink px-5 py-4 flex items-baseline justify-between mt-2 border-[1.5px] border-white/20">
              <span className="text-[12px] font-bold uppercase tracking-widest">Neto për ty</span>
              <span className="text-3xl font-black tracking-tight">{eur(net)}</span>
            </div>
            <p className="text-[12px] text-[hsl(var(--paper)/0.65)] flex items-start gap-2">
              <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
              15% e Krebra-s aplikohet vetëm mbi payout-in real që fiton creator-i. Brand-i para-financon buxhetin; balanca e pashfrytëzuar rimbursohet.
            </p>
          </div>
        </div>
      </div>
      <style>{`
        .calc-input {
          height: 46px;
          width: 100%;
          border-radius: 999px;
          border: 1.5px solid hsl(var(--ink));
          background: hsl(var(--paper));
          padding: 0 1rem;
          font-weight: 700;
          color: hsl(var(--ink));
        }
      `}</style>
    </section>
  );
};

const CalcField = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <label className="block">
    <div className="text-[12px] font-bold uppercase tracking-widest text-ink-soft mb-2">{label}</div>
    {children}
    {hint && <p className="mt-1.5 text-[12px] text-muted-foreground">{hint}</p>}
  </label>
);

const CalcRow = ({ label, value, highlight, muted }: { label: string; value: string; highlight?: boolean; muted?: boolean }) => (
  <div className={`flex items-baseline justify-between ${muted ? "opacity-75" : ""}`}>
    <span className="text-[13px] text-[hsl(var(--paper)/0.75)]">{label}</span>
    <span className={`text-lg font-black tracking-tight ${highlight ? "text-[hsl(var(--acid))]" : ""}`}>{value}</span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  PROCESS                                                            */
/* ------------------------------------------------------------------ */

const STEPS = [
  { icon: FileText, title: "Krijo profilin", body: "Creator-i verifikon 18+ dhe POK; brandi verifikon organizatën dhe VAT." },
  { icon: Send,     title: "Prano kushtet",   body: "Aplikimi dhe pranimi mbyllen mbi kushte të pandryshueshme: tarifë bazë, bonus, tavan, afat." },
  { icon: Eye,      title: "Publiko & mat",   body: "Drafti shqyrtohet privatisht, publikohet, dhe views maten me periudhë 7-ditore." },
  { icon: Wallet,   title: "Paguhu",          body: "Earning-u llogaritet nga formula; pagesa përmes POK me referencë të ndjekshme." },
];

const ProcessSection = () => (
  <section className="py-20 lg:py-24 bg-ink text-[hsl(var(--paper-strong))]">
    <div className="page-container">
      <SectionEyebrow tone="acid">Rrjedha e punës</SectionEyebrow>
      <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight max-w-3xl">
        Katër hapa. Zero zona gri.
      </h2>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <div key={s.title} className="rounded-[22px] border-[1.5px] border-[hsl(var(--paper)/0.2)] p-6 bg-[hsl(var(--ink-soft))]">
            <div className="flex items-center justify-between">
              <span className="h-10 w-10 rounded-xl bg-acid text-ink font-black flex items-center justify-center border-[1.5px] border-[hsl(var(--paper-strong))]">
                {i + 1}
              </span>
              <s.icon className="h-5 w-5 text-[hsl(var(--acid))]" aria-hidden />
            </div>
            <h3 className="mt-6 text-lg font-black tracking-tight">{s.title}</h3>
            <p className="mt-2 text-sm text-[hsl(var(--paper)/0.75)] leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  TRUST                                                              */
/* ------------------------------------------------------------------ */

const TRUST = [
  { icon: Eye, title: "Views të verifikuara", body: "Çdo pagesë bonus mbështetet nga screenshots dhe/ose të dhëna API. Nuk paguajmë për shikime pa provë." },
  { icon: FileText, title: "Kushte të qarta", body: "Bonus, tavan, afat, të drejta përdorimi dhe rregulla eksklusiviteti — të pranuara në aplikim, të pandryshueshme pas pranimit." },
  { icon: ShieldCheck, title: "Buxhet i mbrojtur", body: "Brand-i para-financon maksimumin e mundshëm përpara se fushata të hapet. Creator-at aplikojnë vetëm në fushata të financuara." },
  { icon: Ban, title: "Pa garanci virale", body: "Krebra nuk premton se çdo video do t'i kalojë tavanet. Të paguajmë saktë atë që performon — as më shumë, as më pak." },
];

const TrustSection = () => (
  <section className="py-20 lg:py-28">
    <div className="page-container">
      <SectionEyebrow>Besueshmëria</SectionEyebrow>
      <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-ink max-w-3xl">
        Transparencë që mund ta lexosh.
      </h2>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {TRUST.map((t) => (
          <article key={t.title} className="card-ink p-6 bg-paper-strong h-full">
            <span className="inline-flex h-10 w-10 rounded-xl bg-acid border-[1.5px] border-ink items-center justify-center text-ink">
              <t.icon className="h-5 w-5" aria-hidden />
            </span>
            <h3 className="mt-5 text-lg font-black text-ink tracking-tight">{t.title}</h3>
            <p className="mt-2 text-sm text-ink-soft leading-relaxed">{t.body}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  FAQ                                                                */
/* ------------------------------------------------------------------ */

const FAQ = [
  { q: "Kush mund të aplikojë si creator?", a: "Creator-at 18+ nga Shqipëria ose Kosova, me profil TikTok / Instagram / YouTube aktiv, telefon POK të vlefshëm dhe që kanë pranuar kushtet e Krebra-s." },
  { q: "Si përcaktohet pagesa?", a: "Pagesa bruto = tarifë bazë + min(⌊views/1000⌋ × EUR për 1K, tavan bonusi). Vetëm 1K komplete numërohen. Krebra mban 15% mbi këtë vlerë; pjesa tjetër i shkon creator-it përmes POK." },
  { q: "A garanton Krebra shikime virale?", a: "Jo. Ne garantojmë tarifën bazë kur drafti miratohet dhe llogaritjen e drejtë të bonusit mbi views të verifikuara. Askush nuk mund të premtojë 'viral' në mënyrë të ndershme." },
  { q: "Kush e para-financon buxhetin?", a: "Brandi para-financon buxhetin maksimal të creator-ëve plus rezervën e 15% të Krebra-s përmes POK përpara se fushata të hapet. Balanca e papërdorur rimbursohet automatikisht." },
  { q: "Si i verifikoni views?", a: "Kombinim i screenshot-eve të kërkuara pas dritares së matjes (>=48 orë) dhe të dhënave nga API-t kur creator-i ka lidhur llogarinë e vet Business. Admin-i shqyrton çdo raport." },
];

const FaqPreview = () => (
  <section className="py-20 lg:py-24 bg-paper-strong border-y border-ink/20">
    <div className="page-container grid lg:grid-cols-[1fr_1.4fr] gap-12">
      <div>
        <SectionEyebrow>Pyetje</SectionEyebrow>
        <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-ink">
          Përgjigje të drejtpërdrejta.
        </h2>
        <p className="mt-4 text-ink-soft max-w-md">
          Nëse nuk gjen atë që kërkon këtu, na shkruaj në support@krebra.com dhe përgjigjemi brenda 24 orëve pune.
        </p>
        <Link to="/faq" className="btn-pill mt-6">Të gjitha pyetjet <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <Accordion type="single" collapsible className="card-ink bg-paper-strong divide-y divide-ink/10">
        {FAQ.map((f, i) => (
          <AccordionItem key={i} value={`f${i}`} className="px-5 border-b-0 last:border-b-0">
            <AccordionTrigger className="text-left text-[15px] font-bold text-ink hover:no-underline py-5">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-ink-soft leading-relaxed pb-5">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  FINAL CTA                                                          */
/* ------------------------------------------------------------------ */

const FinalCta = () => (
  <section className="py-24">
    <div className="page-container">
      <div className="card-ink-hard-lg bg-orange text-ink p-10 md:p-16 relative overflow-hidden">
        <div aria-hidden className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-ink/10" />
        <p className="text-[12px] font-bold uppercase tracking-widest">Pilot i hapur — Shqipëri &amp; Kosovë</p>
        <h2 className="mt-3 text-3xl md:text-5xl lg:text-6xl font-black tracking-tight max-w-3xl leading-[1.05]">
          Bëhu pjesë e pilotit të parë të Krebra-s.
        </h2>
        <p className="mt-5 text-lg text-ink/85 max-w-2xl">
          Kërkojmë creator-at e parë të verifikuar dhe brandet e para që lanço fushata të matshme këtë sezon. Instalimi si aplikacion do të vijë së shpejti.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/signup" className="btn-pill btn-pill-ink">Fillo tani <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/contact" className="btn-pill bg-paper-strong">Bisedo me ekipin</Link>
          <button
            type="button"
            className="btn-pill opacity-70 cursor-not-allowed"
            disabled
            aria-disabled="true"
            title="Instalimi si aplikacion do të aktivizohet së shpejti"
          >
            Instalo si aplikacion (së shpejti)
          </button>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  SECTION EYEBROW                                                    */
/* ------------------------------------------------------------------ */

const SectionEyebrow = ({ children, tone = "ink" }: { children: React.ReactNode; tone?: "ink" | "acid" }) => (
  <span className={`inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest ${tone === "acid" ? "text-[hsl(var(--acid))]" : "text-ink-soft"}`}>
    <span className={`h-1.5 w-6 rounded-full ${tone === "acid" ? "bg-acid" : "bg-ink"}`} aria-hidden />
    {children}
  </span>
);

export default Index;
